import { Menu } from '@base-ui/react/menu'
import React from 'react'
import styled, { css } from 'styled-components'
import { ChevronDown } from 'sutra/components/Icons'
import {
  optionStyles,
  popupStyles,
} from 'sutra/components/Select/select-styles'
import { accents, gray } from 'sutra/tokens/colors'

// What the menu hands back on a pick. A real `click` (a keyboard Enter or Space
// on an item is dispatched as one), so `event.stopPropagation()` works for a
// menu inside a clickable row: React bubbles events from a portaled popup
// through its React parents (antd's `domEvent.stopPropagation()`).
export type DropdownMenuEvent = React.MouseEvent<HTMLElement>

export interface DropdownMenuActionItem {
  key: string
  label: React.ReactNode
  // Leading slot, before the label.
  icon?: React.ReactNode
  // Red text. Rendered with `data-danger` on the item.
  danger?: boolean
  // Exposed as `aria-disabled` and `data-disabled` (never a native `disabled`);
  // the item can't be picked.
  disabled?: boolean
  // Runs before the menu-level `onAction`.
  onClick?: (event: DropdownMenuEvent) => void
  // Default true. Pass false to keep the menu open after this item is picked.
  closeOnClick?: boolean
  // Only matters when the menu has a `selectedKey`: false renders this item as
  // a plain action (e.g. "Remove" beside a list of roles) instead of a choice.
  selectable?: boolean
  className?: string
  style?: React.CSSProperties
}

export interface DropdownMenuSubmenuItem {
  key: string
  label: React.ReactNode
  icon?: React.ReactNode
  disabled?: boolean
  // Opens on hover, or on ArrowRight / Enter from the keyboard.
  children: readonly DropdownMenuItem[]
  className?: string
  style?: React.CSSProperties
}

export interface DropdownMenuGroupItem {
  type: 'group'
  key: string
  // The group's heading; also its accessible name.
  label: React.ReactNode
  children: readonly DropdownMenuItem[]
}

export interface DropdownMenuDividerItem {
  type: 'divider'
  key?: string
}

export type DropdownMenuItem =
  | DropdownMenuActionItem
  | DropdownMenuSubmenuItem
  | DropdownMenuGroupItem
  | DropdownMenuDividerItem

export type DropdownMenuSide = 'top' | 'bottom' | 'left' | 'right'
export type DropdownMenuAlign = 'start' | 'center' | 'end'

/**
 * A menu of actions that opens from a trigger. Replaces antd `Dropdown` with
 * `menu={{ items }}`; built on Base UI `Menu`, so the popup is `role="menu"`
 * and the rows are `menuitem` / `menuitemradio`.
 *
 * ```tsx
 * <DropdownMenu
 *   side="bottom"
 *   align="end"
 *   items={[
 *     { key: 'edit', label: 'Edit', icon: <EditIcon /> },
 *     { type: 'divider' },
 *     { key: 'delete', label: 'Delete', danger: true },
 *   ]}
 *   onAction={(key) => handle(key)}
 * >
 *   <KebabButton aria-label="Actions" />
 * </DropdownMenu>
 * ```
 *
 * Picking an item runs its `onClick(event)`, then `onAction(key, event)`, then
 * closes the menu. A disabled item does neither.
 *
 * ## Mapping from antd
 * - `menu.items` -> `items`. Item `type: 'divider' | 'group'` and `children`
 *   (a submenu, with no `type`) carry over; `label`, `icon`, `danger`,
 *   `disabled`, `className`, `style` and a per-item `onClick` do too, but
 *   `onClick` receives the click event itself, not `{ key, domEvent }`.
 * - `menu.onClick({ key, domEvent })` -> `onAction(key, event)`.
 * - `menu.selectedKeys={x ? [x] : []}` -> `selectedKey={x ?? null}`.
 *   `undefined` means the menu has no selection at all.
 * - `menu.expandIcon={null}` -> `expandIcon={null}`.
 * - `placement`: `bottomLeft` = `side="bottom" align="start"` (the default),
 *   `bottomRight` = `side="bottom" align="end"`, `bottom` = `side="bottom"
 *   align="center"`, `topLeft` / `topRight` / `top` likewise with
 *   `side="top"`, `left` / `right` = `side="left"` / `"right"`. antd's
 *   `align.offset: [0, y]` -> `sideOffset={y}` (default 4, antd's).
 * - `open` / `onOpenChange` -> same names. `disabled` -> same.
 * - `overlayClassName` / `rootClassName` / `overlayStyle` / `menu.style` ->
 *   `className` / `style`, which land on the popup (`styled(DropdownMenu)`
 *   restyles it). `getPopupContainer` -> `container`.
 * - `trigger={['click']}` is the default. antd's default was hover; here
 *   `openOnHover` also opens on hover and click still toggles it.
 * - NOT supported: `popupRender` / `dropdownRender` (a custom panel). Use a
 *   Sutra `Popover` for those, since a menu owns arrow-key navigation and
 *   typeahead, which breaks an `<input>` or a form inside it.
 *
 * ## The trigger
 * `children` IS the trigger: Base UI hangs its handlers and ref on it, with no
 * wrapper element. It must forward `ref` and spread unknown props onto its DOM
 * node, or the menu never opens. A non-`<button>` child (a `span`, an antd
 * icon) needs `nativeButton={false}`.
 */
export interface DropdownMenuProps {
  children: React.ReactElement
  items: readonly DropdownMenuItem[]
  // Fires for every picked item, after its own `onClick`.
  onAction?: (key: string, event: DropdownMenuEvent) => void
  // Marks one item as the current choice (`menuitemradio` + `aria-checked`,
  // highlighted). Every action item at every level becomes a choice unless it
  // sets `selectable: false`. `null` = nothing selected yet; `undefined` = no
  // selection. Picking a choice does not change this; the caller owns it.
  selectedKey?: string | null
  side?: DropdownMenuSide
  align?: DropdownMenuAlign
  // Gap between trigger and popup, in px.
  sideOffset?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // Ignores every interaction with the trigger.
  disabled?: boolean
  // Also opens on hover, after 100ms in and out. Click still toggles it.
  openOnHover?: boolean
  // Set to false when `children` renders a non-<button> (a span, an icon).
  nativeButton?: boolean
  // Default false, for antd parity: a modal menu locks page scroll and blocks
  // pointer events outside it, so a click elsewhere only dismisses the menu.
  modal?: boolean
  // Replaces the submenu chevron. `null` renders none.
  expandIcon?: React.ReactNode
  // Where the popup is portaled (default `document.body`).
  container?: HTMLElement | null | React.RefObject<HTMLElement | null>
  'aria-label'?: string
  zIndex?: number
  // Both land on the popup. Its min-width is the trigger's width.
  className?: string
  style?: React.CSSProperties
}

// Above antd modals (1000), popovers (1030) and dropdowns (1050) and level with
// the Sutra Popover, below the Sutra Tooltip (1070), so a tooltip on a row
// inside the menu still clears it.
const DEFAULT_Z_INDEX = 1060

// antd's default `Dropdown` gap between trigger and overlay.
const DEFAULT_SIDE_OFFSET = 4

const HOVER_DELAY = 100

const isGroup = (item: DropdownMenuItem): item is DropdownMenuGroupItem =>
  'type' in item && item.type === 'group'

const isDivider = (item: DropdownMenuItem): item is DropdownMenuDividerItem =>
  'type' in item && item.type === 'divider'

const isSubmenu = (item: DropdownMenuItem): item is DropdownMenuSubmenuItem =>
  'children' in item && !isGroup(item)

const Positioner = styled(Menu.Positioner)<{ $zIndex: number }>`
  z-index: ${({ $zIndex }) => $zIndex};
`

const Popup = styled(Menu.Popup)`
  ${popupStyles}
  min-width: max(var(--anchor-width), 120px);
  max-height: var(--available-height);
  overflow-y: auto;
`

// The row look is Select's option row, so a Select and a menu match; the
// differences are a start-aligned row, a checked state (Base UI radio items say
// `data-checked`, Select options `data-selected`), and the danger tone.
const rowStyles = css`
  justify-content: flex-start;

  &[data-checked] {
    font-weight: 600;
    background: ${accents.reflectionBlue[50]};
  }
  &[data-checked][data-highlighted] {
    background: ${accents.reflectionBlue[100]};
  }
  &[data-popup-open] {
    background: ${accents.reflectionBlue[50]};
  }
  &[data-danger] {
    color: ${accents.crimsonBloom[500]};
  }
  &[data-danger][data-highlighted] {
    background: ${accents.crimsonBloom[50]};
  }
  &[data-disabled] {
    color: ${gray[400]};
    background: transparent;
  }
`

const Item = styled(Menu.Item)`
  ${optionStyles}
  ${rowStyles}
`

const RadioItem = styled(Menu.RadioItem)`
  ${optionStyles}
  ${rowStyles}
`

const SubmenuTrigger = styled(Menu.SubmenuTrigger)`
  ${optionStyles}
  ${rowStyles}
`

const Icon = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
`

const Label = styled.span`
  flex: 1;
  min-width: 0;
`

const Expand = styled(ChevronDown)`
  flex-shrink: 0;
  color: ${gray[500]};
  transform: rotate(-90deg);
`

const GroupLabel = styled(Menu.GroupLabel)`
  padding: 6px 10px 2px;
  font-size: 12px;
  font-weight: 600;
  color: ${gray[500]};
  user-select: none;
`

const Separator = styled(Menu.Separator)`
  height: 1px;
  margin: 4px -4px;
  background: ${gray[200]};
`

interface RenderContext {
  onAction: DropdownMenuProps['onAction']
  selectable: boolean
  expandIcon: React.ReactNode
  positioner: { zIndex: number; container: DropdownMenuProps['container'] }
}

const renderRowContent = (
  item: DropdownMenuActionItem | DropdownMenuSubmenuItem,
): React.ReactNode => (
  <>
    {item.icon && <Icon>{item.icon}</Icon>}
    <Label>{item.label}</Label>
  </>
)

const renderItems = (
  items: readonly DropdownMenuItem[],
  context: RenderContext,
): React.ReactNode =>
  items.map((item, index) => {
    if (isDivider(item)) {
      return <Separator key={item.key ?? `divider-${index}`} />
    }

    if (isGroup(item)) {
      return (
        <Menu.Group key={item.key}>
          <GroupLabel>{item.label}</GroupLabel>
          {renderItems(item.children, context)}
        </Menu.Group>
      )
    }

    if (isSubmenu(item)) {
      return (
        <Menu.SubmenuRoot key={item.key}>
          <SubmenuTrigger
            disabled={item.disabled}
            className={item.className}
            style={item.style}
          >
            {renderRowContent(item)}
            {context.expandIcon === undefined ? <Expand /> : context.expandIcon}
          </SubmenuTrigger>
          <Menu.Portal container={context.positioner.container}>
            <Positioner
              $zIndex={context.positioner.zIndex}
              align="start"
              sideOffset={4}
            >
              <Popup>{renderItems(item.children, context)}</Popup>
            </Positioner>
          </Menu.Portal>
        </Menu.SubmenuRoot>
      )
    }

    const handleClick = (event: DropdownMenuEvent): void => {
      item.onClick?.(event)
      context.onAction?.(item.key, event)
    }
    const rowProps = {
      disabled: item.disabled,
      closeOnClick: item.closeOnClick,
      onClick: handleClick,
      'data-danger': item.danger ? '' : undefined,
      className: item.className,
      style: item.style,
    }

    if (context.selectable && item.selectable !== false) {
      return (
        <RadioItem
          key={item.key}
          value={item.key}
          {...rowProps}
          // Base UI's radio item stays open by default; antd's selectable
          // menu closed on pick.
          closeOnClick={item.closeOnClick ?? true}
        >
          {renderRowContent(item)}
        </RadioItem>
      )
    }
    return (
      <Item key={item.key} {...rowProps}>
        {renderRowContent(item)}
      </Item>
    )
  })

export const DropdownMenu = ({
  children,
  items,
  onAction,
  selectedKey,
  side = 'bottom',
  align = 'start',
  sideOffset = DEFAULT_SIDE_OFFSET,
  open,
  onOpenChange,
  disabled,
  openOnHover = false,
  nativeButton = true,
  modal = false,
  expandIcon,
  container,
  'aria-label': ariaLabel,
  zIndex = DEFAULT_Z_INDEX,
  className,
  style,
}: DropdownMenuProps): React.ReactElement => {
  const content = renderItems(items, {
    onAction,
    selectable: selectedKey !== undefined,
    expandIcon,
    positioner: { zIndex, container },
  })

  return (
    <Menu.Root
      open={open}
      onOpenChange={onOpenChange && ((next) => onOpenChange(next))}
      modal={modal}
    >
      <Menu.Trigger
        render={children}
        nativeButton={nativeButton}
        disabled={disabled}
        openOnHover={openOnHover}
        delay={HOVER_DELAY}
        closeDelay={HOVER_DELAY}
      />
      <Menu.Portal container={container}>
        <Positioner
          $zIndex={zIndex}
          side={side}
          align={align}
          sideOffset={sideOffset}
        >
          <Popup
            aria-label={ariaLabel}
            className={className}
            style={style}
            data-sutra-dropdown-menu=""
          >
            {selectedKey === undefined ? (
              content
            ) : (
              <Menu.RadioGroup value={selectedKey}>{content}</Menu.RadioGroup>
            )}
          </Popup>
        </Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

DropdownMenu.displayName = 'DropdownMenu'
